import Service from '@ember/service';

import {
  MOCK_ASSESSMENT_QUESTIONS,
  MOCK_CAREER_ROLES,
  MOCK_LRAP_PROGRAMS,
  getCareerCapitalScoreForClient,
  getCareerFamiliesForClient,
  getCareerPlanForClient,
  getCareerRolesForClient,
  getEmployeeProfileForClient,
  getFeaturedRolesForClient,
  getFinancialSnapshotForClient,
  getLeaderboardForClient,
  getLrapDiscoveryForClient,
  getPslfJourneyForClient,
  getPslfVaultForClient,
  getRecommendationsForClient,
  getScoreHistoryForClient,
  getSection127RecommendationForClient,
  getSkillGapsForClient,
  getStackingOptimizationForClient,
  getSuccessStoriesForClient,
  getSummaryForClient,
} from 'tio-employee/utils/career-pathways/mock-data';

import type {
  AssessmentQuestion,
  CareerCapitalScore,
  CareerFamily,
  CareerPlan,
  CareerRole,
  CareerSummary,
  EmployeeCareerProfile,
  EmployeeSkill,
  FinancialSnapshot,
  LeaderboardRank,
  LrapDiscoverySummary,
  LrapEligibility,
  LrapEnrollment,
  LrapProgram,
  LrapProgramType,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfPaymentValidation,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  Recommendation,
  ScoreHistoryPoint,
  Section127Allocation,
  Section127Recommendation,
  SkillGap,
  StackingOptimization,
  SuccessStory,
} from 'tio-employee/utils/career-pathways/types';

// Re-export all types for backwards compatibility
export type {
  AssessmentOption,
  AssessmentQuestion,
  Badge,
  CareerCapitalScore,
  CareerFamily,
  CareerPlan,
  CareerRole,
  CareerSummary,
  ComponentScore,
  EmployeeCareerProfile,
  EmployeeSkill,
  EmployerContributions,
  FinancialSnapshot,
  LeaderboardRank,
  LearningPath,
  LrapDiscoverySummary,
  LrapEligibility,
  LrapEligibilityStatus,
  LrapEnrollment,
  LrapProgram,
  LrapProgramType,
  Milestone,
  OptimizationFactor,
  OptimizationPotential,
  OutsideFundsConflict,
  ProjectedImpact,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfMilestone,
  PslfPaymentValidation,
  PslfPolicyUpdate,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfQualificationCheck,
  PslfRiskFactor,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  Recommendation,
  RecommendedAction,
  RoleSkill,
  ScoreComponents,
  ScoreFactor,
  ScoreHistoryPoint,
  ScoreImprovement,
  ScoreLevel,
  ScoreMilestone,
  Section127Allocation,
  Section127Recommendation,
  SkillGap,
  StackingAllocation,
  StackingOptimization,
  StudentLoanSummary,
  SuccessStory,
} from 'tio-employee/utils/career-pathways/types';

/**
 * Career Pathways API Service
 *
 * This service provides mock API responses for career pathways data.
 * In production, these will be replaced with actual API calls to tio-api.
 *
 * Types are defined in 'tio-employee/utils/career-pathways/types'
 * Mock data helpers are in 'tio-employee/utils/career-pathways/mock-data'
 */
export default class CareerPathwaysApiService extends Service {
  // Simulate network latency (300-600ms)
  private simulateDelay(): Promise<void> {
    const delay = 300 + Math.random() * 300;
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  // ==========================================================================
  // DASHBOARD / INDEX
  // ==========================================================================

  async fetchDashboardData(): Promise<{
    employee: EmployeeCareerProfile;
    careerPlan: CareerPlan;
    recommendations: Recommendation[];
    summary: CareerSummary;
  }> {
    await this.simulateDelay();
    return {
      employee: getEmployeeProfileForClient(),
      careerPlan: getCareerPlanForClient(),
      recommendations: getRecommendationsForClient().slice(0, 3),
      summary: getSummaryForClient(),
    };
  }

  // ==========================================================================
  // EXPLORE
  // ==========================================================================

  async fetchCareerFamilies(): Promise<CareerFamily[]> {
    await this.simulateDelay();
    return getCareerFamiliesForClient();
  }

  async fetchFeaturedRoles(): Promise<CareerRole[]> {
    await this.simulateDelay();
    return getFeaturedRolesForClient();
  }

  async fetchCareerRole(roleId: string): Promise<CareerRole | null> {
    await this.simulateDelay();
    return getCareerRolesForClient().find((r) => r.id === roleId) || null;
  }

  async fetchRolePathways(roleId: string): Promise<{ from: CareerRole[]; to: CareerRole[] }> {
    await this.simulateDelay();
    const roles = getCareerRolesForClient();
    const role = roles.find((r) => r.id === roleId);
    if (!role) return { from: [], to: [] };

    const from = roles.filter((r) => role.typicalPath.includes(r.id));
    const to = roles.filter((r) => role.nextSteps.includes(r.id));
    return { from, to };
  }

  async fetchAllRolesForComparison(): Promise<CareerRole[]> {
    await this.simulateDelay();
    return getCareerRolesForClient();
  }

  // ==========================================================================
  // SKILLS
  // ==========================================================================

  async fetchEmployeeSkills(): Promise<{
    skills: EmployeeSkill[];
    summary: CareerSummary;
  }> {
    await this.simulateDelay();
    return {
      skills: getEmployeeProfileForClient().skills,
      summary: getSummaryForClient(),
    };
  }

  async fetchSkillGaps(_targetRoleId?: string): Promise<SkillGap[]> {
    await this.simulateDelay();
    return getSkillGapsForClient();
  }

  async fetchAssessmentQuestions(_skillId: string): Promise<AssessmentQuestion[]> {
    await this.simulateDelay();
    return MOCK_ASSESSMENT_QUESTIONS;
  }

  // ==========================================================================
  // RECOMMENDATIONS
  // ==========================================================================

  async fetchRecommendations(filters?: {
    taEligibleOnly?: boolean;
    type?: string;
    sortBy?: string;
  }): Promise<Recommendation[]> {
    await this.simulateDelay();
    let results = [...getRecommendationsForClient()];

    if (filters?.taEligibleOnly) {
      results = results.filter((r) => r.taEligible);
    }
    if (filters?.type && filters.type !== 'all') {
      results = results.filter((r) => r.type.toLowerCase() === filters.type);
    }

    return results;
  }

  // ==========================================================================
  // CAREER PLAN
  // ==========================================================================

  async fetchCareerPlan(): Promise<CareerPlan | null> {
    await this.simulateDelay();
    return getCareerPlanForClient();
  }

  async fetchAvailableGoals(): Promise<CareerRole[]> {
    await this.simulateDelay();
    return MOCK_CAREER_ROLES;
  }

  async createCareerPlan(targetRoleId: string, targetDate: string): Promise<CareerPlan> {
    await this.simulateDelay();
    // In production, this would POST to the API
    return {
      ...getCareerPlanForClient(),
      id: `plan-${Date.now()}`,
      targetRole: targetRoleId,
      createdAt: new Date().toISOString(),
      targetDate,
    };
  }

  // ==========================================================================
  // SUCCESS STORIES
  // ==========================================================================

  async fetchSuccessStories(filters?: {
    department?: string;
    featured?: boolean;
  }): Promise<SuccessStory[]> {
    await this.simulateDelay();
    let results = [...getSuccessStoriesForClient()];

    if (filters?.featured) {
      results = results.filter((s) => s.featured);
    }
    if (filters?.department) {
      results = results.filter((s) =>
        s.department.toLowerCase().includes(filters.department?.toLowerCase() ?? '')
      );
    }

    return results;
  }

  // ==========================================================================
  // AI ADVISOR (would connect to tio-ai-agent)
  // ==========================================================================

  async sendAdvisorMessage(
    _message: string,
    _sessionId: string
  ): Promise<{
    response: string;
    suggestions: string[];
  }> {
    await this.simulateDelay();
    const employee = getEmployeeProfileForClient();
    const targetRole = employee.targetRole?.title || 'your target role';
    // In production, this would POST to tio-ai-agent service
    return {
      response: `Based on your career goal of becoming ${targetRole}, I recommend focusing on the top skill gaps first. The highest-match program in your recommendations is fully covered by your TA benefit.`,
      suggestions: [
        'Tell me more about the top recommended program',
        `What skills do I need for ${targetRole}?`,
        'Show me my progress so far',
      ],
    };
  }

  // ==========================================================================
  // SECTION 127 OPTIMIZER (NEW)
  // ==========================================================================

  async fetchFinancialSnapshot(): Promise<FinancialSnapshot> {
    await this.simulateDelay();
    return getFinancialSnapshotForClient();
  }

  async fetchSection127Recommendation(): Promise<Section127Recommendation> {
    await this.simulateDelay();
    return getSection127RecommendationForClient();
  }

  async updateSection127Allocation(allocation: {
    loanAllocation: number;
    tuitionAllocation: number;
    strategy: Section127Allocation['strategy'];
  }): Promise<Section127Allocation> {
    await this.simulateDelay();
    // In production, this would PATCH to the API
    const snapshot = getFinancialSnapshotForClient();
    return {
      ...snapshot.section127,
      loanAllocation: allocation.loanAllocation,
      tuitionAllocation: allocation.tuitionAllocation,
      strategy: allocation.strategy,
      updatedAt: new Date().toISOString(),
    };
  }

  // ==========================================================================
  // CAREER CAPITAL SCORE (NEW)
  // ==========================================================================

  async fetchCareerCapitalScore(): Promise<CareerCapitalScore> {
    await this.simulateDelay();
    return getCareerCapitalScoreForClient();
  }

  async fetchScoreHistory(months: number = 12): Promise<ScoreHistoryPoint[]> {
    await this.simulateDelay();
    return getScoreHistoryForClient(months);
  }

  async fetchLeaderboard(groupType: 'team' | 'department' | 'company'): Promise<{
    entries: Array<{
      rank: number;
      name: string;
      score: number;
      change: number;
      isCurrentUser: boolean;
    }>;
    currentUserRank: LeaderboardRank;
  }> {
    await this.simulateDelay();
    return getLeaderboardForClient(groupType);
  }

  // ==========================================================================
  // LRAP DISCOVERY & STACKING (NEW)
  // ==========================================================================

  async fetchLrapDiscovery(): Promise<LrapDiscoverySummary> {
    await this.simulateDelay();
    return getLrapDiscoveryForClient();
  }

  /**
   * Synchronous fetch for LRAP discovery - demo purposes only.
   * In production, this would be loaded via route model and passed as args.
   */
  fetchLrapDiscoverySyncForDemo(): LrapDiscoverySummary {
    return getLrapDiscoveryForClient();
  }

  async fetchLrapPrograms(filters?: {
    type?: LrapProgramType;
    state?: string;
  }): Promise<LrapProgram[]> {
    await this.simulateDelay();
    let programs = [...MOCK_LRAP_PROGRAMS];
    if (filters?.type) {
      programs = programs.filter((p) => p.type === filters.type);
    }
    return programs;
  }

  async checkLrapEligibility(programId: string): Promise<LrapEligibility | null> {
    await this.simulateDelay();
    const discovery = getLrapDiscoveryForClient();
    return discovery.eligiblePrograms.find((e) => e.programId === programId) || null;
  }

  async enrollInLrap(programId: string): Promise<LrapEnrollment> {
    await this.simulateDelay();
    const program = MOCK_LRAP_PROGRAMS.find((p) => p.id === programId);
    return {
      id: `enrollment-${Date.now()}`,
      programId,
      programName: program?.name || 'Unknown Program',
      programType: program?.type || 'employer',
      enrolledDate: new Date().toISOString(),
      annualBenefit: program?.maxAnnual || 0,
      ytdReceived: 0,
      lifetimeReceived: 0,
      nextPaymentDate: null,
      status: 'pending',
      expirationDate: null,
    };
  }

  async updateLrapDiscoveryStatus(completed: boolean): Promise<void> {
    await this.simulateDelay();
    // In production, this would PATCH to the API
    console.log('LRAP Discovery status updated:', completed);
  }

  async fetchStackingOptimization(): Promise<StackingOptimization> {
    await this.simulateDelay();
    return getStackingOptimizationForClient();
  }

  // ==========================================================================
  // PSLF COMPLIANCE VAULT (NEW)
  // ==========================================================================

  async fetchPslfVault(): Promise<PslfVaultSummary> {
    await this.simulateDelay();
    return getPslfVaultForClient();
  }

  async fetchPslfProgress(): Promise<PslfProgress> {
    await this.simulateDelay();
    const vault = getPslfVaultForClient();
    return vault.progress;
  }

  async uploadPslfDocument(document: {
    formType: PslfVaultDocument['formType'];
    file: File;
    employerName: string;
    coversPeriod: { start: string; end: string };
  }): Promise<PslfVaultDocument> {
    await this.simulateDelay();
    // In production, this would POST to the API with file upload
    return {
      id: `doc-${Date.now()}`,
      formType: document.formType,
      filename: document.file.name,
      uploadedAt: new Date().toISOString(),
      certifiedDate: null,
      employerName: document.employerName,
      coversPeriod: document.coversPeriod,
      paymentsCertified: 0,
      status: 'submitted',
      rejectionReason: null,
      downloadUrl: '#',
    };
  }

  async generatePslfForm(
    _formType: 'ecf'
  ): Promise<{ downloadUrl: string; prefilledData: object }> {
    await this.simulateDelay();
    // In production, this would generate a pre-filled ECF form
    return {
      downloadUrl: '#generated-form',
      prefilledData: {
        employerName: 'Current Employer',
        employerEIN: '12-3456789',
        employmentStartDate: '2022-01-15',
      },
    };
  }

  // ==========================================================================
  // PSLF REIMAGINED - FORGIVENESS ACCELERATOR
  // ==========================================================================

  async fetchPslfJourney(): Promise<PslfJourney> {
    await this.simulateDelay();
    return getPslfJourneyForClient();
  }

  async fetchPslfProjection(): Promise<PslfProjection> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.projection;
  }

  async fetchPslfPayments(): Promise<{
    payments: PslfPaymentValidation[];
    summary: { qualifying: number; pending: number; nonQualifying: number };
  }> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return {
      payments: journey.recentPayments,
      summary: journey.allPaymentsCount,
    };
  }

  async fetchPslfPaymentDetail(paymentId: string): Promise<PslfPaymentValidation | null> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.recentPayments.find((p) => p.id === paymentId) ?? null;
  }

  async fetchPslfAutomationStatus(): Promise<PslfAutomationStatus> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.automationStatus;
  }

  async fetchPslfProtectionStatus(): Promise<PslfProtectionStatus> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.protectionStatus;
  }

  async fetchPslfAchievements(): Promise<{
    achievements: PslfAchievement[];
    nextAchievement: PslfAchievement | null;
  }> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return {
      achievements: journey.achievements,
      nextAchievement: journey.nextAchievement,
    };
  }

  async fetchPslfScenarios(): Promise<PslfScenario[]> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.scenarios;
  }

  async calculatePslfScenario(
    scenarioType: PslfScenario['type'],
    _params?: Record<string, unknown>
  ): Promise<PslfScenario> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    const scenario = journey.scenarios.find((s) => s.type === scenarioType);
    if (!scenario) {
      throw new Error(`Unknown scenario type: ${scenarioType}`);
    }
    return scenario;
  }

  async celebratePslfMilestone(milestoneId: string): Promise<PslfAchievement | null> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.achievements.find((a) => a.id === milestoneId) ?? null;
  }
}
